import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  NgZone, OnInit, OnDestroy,
  ViewChild
} from '@angular/core';
import {MatCardModule} from "@angular/material/card";
import {MatIconModule} from "@angular/material/icon";
import {MatToolbarModule} from "@angular/material/toolbar";
import {MatButtonModule} from "@angular/material/button";
import {MatMenuModule} from "@angular/material/menu";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {DOCUMENT, NgStyle} from "@angular/common";
import {HttpClient} from "@angular/common/http";
import {MatBottomSheet} from "@angular/material/bottom-sheet";
import {Title} from "@angular/platform-browser";
import {MidiPlayerService} from "../service/midi-player.service";
import {HymnSelectionService} from "../service/hymn-selection.service";
import {LayoutService} from "../service/layout.service";
import {ShareService} from "../service/share.service";
import {SidenavService} from "../service/sidenav.service";
import {SettingsService} from "../service/settings.service";
import {HymnSection} from "../hymn/HymnSection";
import {hymnsDetailed} from "../hymn/hymnsDetailed";
import {fromEvent} from "rxjs";
import {debounceTime} from "rxjs/operators";
import {FontSizeComponent} from "../controls/font-size/font-size.component";
import {MusicControlComponent} from "../controls/music-control/music-control.component";
import {ModalService} from "../service/modal.service";
import {Hymn} from "../hymn/hymn";
import {translation} from "../translation";
import {DoubleTapDirective} from "../directive/double-tap.directive";
import {SwipeGestureDirective} from "../directive/swipe-gesture.directive";
import {PinchZoomDirective} from "../directive/pinch-zoom.directive";
import {MatSnackBar, MatSnackBarModule} from "@angular/material/snack-bar";
import {KaraokeMarkupPipe} from "../pipes/karaoke-markup.pipe";
import {PresentationAutoScrollService} from "../service/presentation-auto-scroll.service";
import {ContinuousPlaybackService} from "../service/continuous-playback.service";

@Component({
  selector: 'app-hymn-presenter',
  standalone: true,
  imports: [
    MatIconModule,
    MatCardModule,
    MatToolbarModule,
    MatButtonModule,
    MatMenuModule,
    RouterLink,
    NgStyle,
    MusicControlComponent,
    DoubleTapDirective,
    SwipeGestureDirective,
    PinchZoomDirective,
    MatSnackBarModule,
    KaraokeMarkupPipe
  ],
  templateUrl: './hymn-presenter.component.html',
  styleUrl: './hymn-presenter.component.scss'
})
export class HymnPresenterComponent implements OnInit, AfterViewInit, OnDestroy {

  // Whether this hymn has karaoke markers usable for presentation autoscroll/karaoke
  public hasPresentationMarkers: boolean = true;

  lastHymnNumber = hymnsDetailed[hymnsDetailed.length - 1].hymnNumber;

  Number = Number;

  @ViewChild('content') public content!: ElementRef;
  @ViewChild('hymnTitle') public hymnTitle!: ElementRef;
  @ViewChild('verseNumber') public hymnNumber!: ElementRef;
  @ViewChild('hymnText') public hymnText!: ElementRef;
  contentHeight!: number;
  containerHeight!: number;
  width!: number;

  hymn: any = null;
  elem: any;

  verseList: any[] = [];
  activeVerseIndex = 0;
  activeVerse = '1';

  fontsize = 12;

  swipeThreshold = 70;

  maxFontSize: number | null;
  crossVerseConsistentFontSize: number | null;

  activeVerseLines: string[] = [];
  visibleVerseSections = new Map<number, HymnSection>();
  linesPerVerse = this.settingService.getPresentationModeVisibleLines();
  linesPerVerseBase = this.linesPerVerse;
  visibleSectionIndex = 0;

  protected textSizeStyle = this.fontsize + 'px';
  fontSizeMax: number = 100;
  fontSizeMin: number = 8;

  presentFontFamily: string | undefined = this.settingService.getFontFamily();
  presentFontWeight: number | undefined = this.settingService.getFontWeight();

  private karaokeIndex: Map<string, HTMLElement[]> = new Map();
  private activeKeys = new Set<string>();
  private keyToElements = new Map<string, HTMLElement[]>();
  private subscriptions: any[] = [];

  private currentVerseSectionMap = new Map<string, number>();
  private autoJumpTimeout: any;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private http: HttpClient,
    private route: ActivatedRoute,
    private ngZone: NgZone,
    private bottomSheet: MatBottomSheet,
    private titleService: Title,
    public router: Router,
    public midiService: MidiPlayerService,
    public hymnSelectionService: HymnSelectionService,
    public layoutService: LayoutService,
    public shareService: ShareService,
    public sidenavService: SidenavService,
    protected settingService: SettingsService,
    protected modalService: ModalService,
    private ref: ChangeDetectorRef,
    private snackBar: MatSnackBar,
    protected presentationAutoScrollService: PresentationAutoScrollService,
    protected continuousPlaybackService: ContinuousPlaybackService) {

    this.maxFontSize = null;
    this.crossVerseConsistentFontSize = null;

    fromEvent(window, 'resize').pipe(debounceTime(100)).subscribe(() => {
      this.onResize();
    });
  }

  toggleListSidenav() {
    this.sidenavService.toggleList();
  }

  onSwipePrevious() {
    if (this.shouldBlockNavigation()) {
      this.showNavigationBlockedMessage();
      return;
    }
    this.navigateLeft();
  }

  onSwipeNext() {
    if (this.shouldBlockNavigation()) {
      this.showNavigationBlockedMessage();
      return;
    }
    this.navigateRight();
  }

  toggleSettingSidenav() {
    this.settingService.toggleSettings();
  }

  ngOnInit() {
    this.subscriptions.push(
      this.settingService.fontFamilySubject.subscribe((f) => {
        this.ngZone.run(() => {
          this.presentFontFamily = f;
        });
      })
    );

    this.subscriptions.push(
      this.settingService.fontWeightSubject.subscribe((w) => {
        this.ngZone.run(() => {
          this.presentFontWeight = (w ?? 400);
        });
      })
    );

    this.subscriptions.push(
      this.presentationAutoScrollService.mode$.subscribe((m) => {
        if (m !== 'karaoke') {
          this.ngZone.runOutsideAngular(() => this.clearActive());
        }
      })
    );

    const mode = this.settingService.getPresentationAutoscroll();
    this.presentationAutoScrollService.setMode(mode);

    this.subscriptions.push(
      this.midiService.event$.subscribe(ev => {
        if (!ev) return;

        if (ev.type === 'note') {
          const autoScrollOk = (this.presentationAutoScrollService.enabled || this.presentationAutoScrollService.active);
          if (!autoScrollOk) return;
          if (this.midiService.introPlaying) return;

          const mapKey = `${ev.track}:${ev.index}`;
          const isLyricNote = this.currentVerseSectionMap.has(mapKey);

          if (ev.state === 'on') {
            if (isLyricNote) {
              clearTimeout(this.autoJumpTimeout);

              const expectedSection = this.currentVerseSectionMap.get(mapKey)!;
              let autocorrected = false;

              if (expectedSection !== this.visibleSectionIndex) {
                if (this.presentationAutoScrollService.mode !== 'manual') {
                  autocorrected = true;
                  this.ngZone.run(() => {
                    this.visibleSectionIndex = expectedSection;
                    this.ref.detectChanges();
                    this.resetAndCheckFontSize();

                    setTimeout(() => {
                      this.rebuildKaraokeIndex();
                      if (this.presentationAutoScrollService.mode === 'karaoke') {
                        this.setHighlight(ev.track, ev.index, true);
                      }
                    }, 50);
                  });
                }
              }

              if (!autocorrected && this.presentationAutoScrollService.mode === 'karaoke') {
                this.ngZone.runOutsideAngular(() => this.setHighlight(ev.track, ev.index, true));
              }

              if (!autocorrected && this.presentationAutoScrollService.mode !== 'manual') {
                if (this.isLastNoteOfSection(ev.track, ev.index)) {
                  const snapshotVerseIndex = this.activeVerseIndex;
                  const snapshotSectionIndex = this.visibleSectionIndex;

                  this.autoJumpTimeout = setTimeout(() => {
                    if (this.presentationAutoScrollService.active || this.presentationAutoScrollService.enabled) {
                      this.ngZone.run(() => {
                        if (this.activeVerseIndex === snapshotVerseIndex && this.visibleSectionIndex === snapshotSectionIndex) {
                          this.performNavigationRight();
                        }
                      });
                    }
                  }, 500);
                }
              }
            }

          } else if (ev.state === 'off') {
            if (isLyricNote && this.presentationAutoScrollService.mode === 'karaoke') {
              this.ngZone.runOutsideAngular(() => this.setHighlight(ev.track, ev.index, false));
            }
          }

        } else if (ev.type === 'status') {
          clearTimeout(this.autoJumpTimeout);
          const s = ev.status;

          if (s.startsWith('verse_changed:')) {
            if (this.presentationAutoScrollService.mode !== 'manual') {
              const vNum = s.split(':')[1];
              const targetIndex = this.verseList.indexOf(vNum);
              if (targetIndex !== -1 && this.activeVerseIndex !== targetIndex) {
                this.ngZone.run(() => {
                  this.activeVerseIndex = targetIndex;
                  this.visibleSectionIndex = 0;
                  this.setActiveVerse();
                  this.ref.detectChanges();
                  this.resetAndCheckFontSize();
                });
              }
            }
          } else if (s === 'chorus_start') {
            if (this.presentationAutoScrollService.mode !== 'manual') {
              let targetIndex = this.verseList.indexOf('R', this.activeVerseIndex);
              if (targetIndex === -1) targetIndex = this.verseList.indexOf('R');

              if (targetIndex !== -1 && this.activeVerseIndex !== targetIndex) {
                this.ngZone.run(() => {
                  this.activeVerseIndex = targetIndex;
                  this.visibleSectionIndex = 0;
                  this.setActiveVerse();
                  this.ref.detectChanges();
                  this.resetAndCheckFontSize();
                });
              }
            }
          } else if (s === 'playing') {
            if (this.presentationAutoScrollService.mode !== 'manual') {
              this.ngZone.run(() => {
                this.activeVerseIndex = 0;
                this.visibleSectionIndex = 0;
                this.setActiveVerse();
                this.ref.detectChanges();
                this.resetAndCheckFontSize();
              });
            }
          } else if (s === 'stopped' || s === 'ended') {
            this.ngZone.runOutsideAngular(() => this.clearActive());
          }
        }
      })
    );

    this.subscriptions.push(
      this.sidenavService.sidenavState$.subscribe(() => {
        this.ngZone.run(() => {
          this.crossVerseConsistentFontSize = null;
          setTimeout(() => this.resetAndCheckFontSize(), 500);
        });
      })
    );

    this.subscriptions.push(
      this.settingService.settingsState$.subscribe(() => {
        this.ngZone.run(() => {
          this.crossVerseConsistentFontSize = null;
          setTimeout(() => this.resetAndCheckFontSize(), 500);
        });
      })
    );

    this.settingService.setPresentationViewMode();
    this.linesPerVerseBase = this.settingService.getPresentationModeVisibleLines();
    this.linesPerVerse = this.linesPerVerseBase;
    this.elem = document.documentElement;
    this.initialize();
    this.maxFontSize = null;
    this.crossVerseConsistentFontSize = null;
  }

  ngAfterViewInit(): void {
    const el = this.content?.nativeElement as HTMLElement | undefined;
    if (el && (!el.style.touchAction || el.style.touchAction === 'auto')) {
      el.style.touchAction = 'pan-y';
    }
    setTimeout(() => this.rebuildKaraokeIndex(), 0);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => { try { s.unsubscribe?.(); } catch {} });
    this.clearActive();
    clearTimeout(this.autoJumpTimeout);
    this.midiService.stop();
  }

  private rebuildKaraokeIndex(): void {
    // Sync presentation mode with user's persisted default first (discard any previous hymn's fallback)
    const defaultMode = this.settingService.getPresentationAutoscroll();
    this.presentationAutoScrollService.setMode(defaultMode);

    this.clearActive();
    this.karaokeIndex.clear();
    this.keyToElements.clear();

    const container = this.hymnText?.nativeElement as HTMLElement | null;
    if (!container) return;

    const spans = container.querySelectorAll('.k-chunk[data-k]');

    // Update availability and enforce per-hymn fallback: presenter relies on markers for both autoscroll and karaoke
    this.hasPresentationMarkers = spans.length > 0;
    if (!this.hasPresentationMarkers && this.presentationAutoScrollService.mode !== 'manual') {
      // Temporarily switch to manual for this hymn without persisting the preference
      this.presentationAutoScrollService.setMode('manual');
      this.presentationAutoScrollService.stopAutoScroll();
    }
    spans.forEach((el: Element) => {
      const data = (el as HTMLElement).dataset['k'] || el.getAttribute('data-k') || '';
      if (!data) return;

      const parts = data.split('|').map(s => s.trim()).filter(Boolean);
      for (const key of parts) {
        const arr = this.karaokeIndex.get(key) || [];
        arr.push(el as HTMLElement);
        this.karaokeIndex.set(key, arr);
        this.keyToElements.set(key, this.karaokeIndex.get(key)!);
      }
    });
  }

  private clearActive(): void {
    if (this.activeKeys.size === 0) return;
    for (const key of Array.from(this.activeKeys)) {
      const els = this.karaokeIndex.get(key) || [];
      for (const el of els) el.classList.remove('k-active');
    }
    this.activeKeys.clear();
  }

  private isInActiveSection(el: HTMLElement): boolean {
    return true;
  }

  private setHighlight(track: number, index: number, on: boolean): void {
    if (!this.hymnText) return;
    const key = `t:${track}:${index}`;
    const all = this.karaokeIndex.get(key) || [];
    const els = all.filter(el => this.isInActiveSection(el));

    if (on) {
      if (!this.activeKeys.has(key)) {
        for (const el of els) el.classList.add('k-active');
        this.activeKeys.add(key);
      }
    } else {
      if (this.activeKeys.has(key)) {
        for (const el of els) el.classList.remove('k-active');
        this.activeKeys.delete(key);
      }
    }
  }

  private isLastNoteOfSection(track: number, index: number): boolean {
    let maxIdx = -1;
    const prefix = `${track}:`;

    for (const [key, section] of this.currentVerseSectionMap.entries()) {
      if (section === this.visibleSectionIndex && key.startsWith(prefix)) {
        const idx = Number(key.split(':')[1]);
        if (idx > maxIdx) maxIdx = idx;
      }
    }

    return index === maxIdx;
  }

  private getLinesForVerse(verseId: string): string[] | undefined {
    if (!this.hymn) return undefined;
    return (verseId !== 'R' ? this.hymn.verses[Number(verseId) - 1]?.content : Hymn.getChorus(this.hymn, Number(verseId+1)));
  }

  initialize() {
    this.route.paramMap.subscribe(params => {
      if (params.get('reference')) {
        this.hymnSelectionService.selectedHymn = hymnsDetailed[+params.get('reference')! - 1];
        this.getJSON(params.get('reference'));
      } else {
        this.hymn = this.settingService.getLastHymn();
        this.hymnSelectionService.selectedHymn = this.hymn;
      }
      this.setUpVerseOrder();
      this.loadActiveVerseLines();
      this.titleService.setTitle(`${translation.hymn} ${this.hymn.hymnNumber} - ${this.hymn.title} - ${translation.title_full}`);
      this.crossVerseConsistentFontSize = null;
      this.resetAndCheckFontSize();
      this.midiService.stop();
    });
  }

  setUpVerseOrder() {
    this.verseList = [];

    this.activeVerseIndex = 0;
    this.visibleSectionIndex = 0;

    if(this.hymn.startsWithChorus) {
      this.verseList.push('R');
      this.activeVerse = 'R';
    } else {
      this.activeVerse = '1';
    }

    let count: number;
    for (count = 1; count <= this.hymn.numberOfVerses; count++) {
      this.verseList.push(String(count));
      if (this.hymn.hasChorus) {
        this.verseList.push('R');
      }
    }
  }


  setHeights() {
    this.textSizeStyle = this.fontsize + 'px';
  }

  clickVerse(verseNumber: string) {
    if (verseNumber === 'R') {
      if (this.activeVerseIndex < this.verseList.length - 1) {
        this.activeVerseIndex++;
        this.visibleSectionIndex = 0;
      }
    } else {
      const targetIndex = this.verseList.indexOf(verseNumber);
      if (targetIndex !== -1) {
        this.activeVerseIndex = targetIndex;
        this.visibleSectionIndex = 0;
      }
    }

    this.setActiveVerse();
    setTimeout(() => { this.ref.detectChanges(); }, 1);
    this.resetAndCheckFontSize();
  }

  onVerseButtonClick(verseNumber: string) {
    if (this.shouldBlockNavigation()) {
      this.showNavigationBlockedMessage();
    } else {
      this.clickVerse(verseNumber);
    }
  }

  shouldBlockNavigation(): boolean {
    return this.presentationAutoScrollService.mode !== 'manual' && this.midiService.playing;
  }

  showNavigationBlockedMessage(): void {
    const message = translation.autoscroll_enabled_disable_verse_buttons || 'Navigation is disabled during autoscroll.';

    const snackRef = this.snackBar.open(message, 'MANUAL', { duration: 4500 });
    snackRef.onAction().subscribe(() => {
      this.presentationAutoScrollService.setMode('manual');
      try { this.settingService.setPresentationAutoscroll('manual'); } catch(e) {}
    });
  }

  public setActiveVerse() {
    this.activeVerse = this.verseList[this.activeVerseIndex];
    this.loadActiveVerseLines();
  }

  public getJSON(reference: string | null) {
    if(reference) {
      this.hymn = hymnsDetailed[+reference - 1];
    }
  }

  toggleFullscreen() {
    if (this.layoutService.fullscreen) {
      this.layoutService.exitFullScreen();
    }
    else {
      this.layoutService.enterFullScreen();
    }
  }

  navigateLeft() {
    if (this.shouldBlockNavigation()) {
      this.showNavigationBlockedMessage();
      return;
    }
    this.performNavigationLeft();
  }

  navigateRight() {
    if (this.shouldBlockNavigation()) {
      this.showNavigationBlockedMessage();
      return;
    }
    this.performNavigationRight();
  }

  private performNavigationLeft() {
    if (this.visibleSectionIndex > 0) {
      this.visibleSectionIndex--;
      setTimeout(() => { this.ref.detectChanges(); }, 1);
      this.resetAndCheckFontSize();

      setTimeout(() => this.rebuildKaraokeIndex(), 50);
    } else if (this.activeVerseIndex > 0) {
      this.activeVerseIndex--;
      this.setActiveVerse();
      this.visibleSectionIndex = this.visibleVerseSections.size-1;
      setTimeout(() => { this.ref.detectChanges(); }, 1);
      this.resetAndCheckFontSize();
    }
  }

  private performNavigationRight() {
    if (this.visibleSectionIndex < this.visibleVerseSections.size - 1) {
      this.visibleSectionIndex++;
      setTimeout(() => { this.ref.detectChanges(); }, 1);
      this.resetAndCheckFontSize();

      setTimeout(() => this.rebuildKaraokeIndex(), 50);
    } else if (this.activeVerseIndex < (this.verseList.length - 1)) {
      this.activeVerseIndex++;
      this.visibleSectionIndex = 0;
      this.setActiveVerse();
      setTimeout(() => { this.ref.detectChanges(); }, 1);
      this.resetAndCheckFontSize();
    }
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    this.ngZone.run(() => {
      if (!this.hymnSelectionService.searchFocus && !this.hymnSelectionService.dialpadActive) {
        if (this.continuousPlaybackService.active) {
          if (event.key === 'ArrowLeft') {
            this.continuousPlaybackService.previous();
            return;
          }
          if (event.key === 'ArrowRight') {
            this.continuousPlaybackService.next();
            return;
          }
        }
        switch (event.key) {
          case 'ArrowLeft':
          case 'b':
            this.navigateLeft();
            break;
          case 'ArrowRight':
          case 'n':
            this.navigateRight();
            break;
          case 'r':
          case 'R':
            if (this.shouldBlockNavigation()) {
              this.showNavigationBlockedMessage();
            } else if (this.hymn.hasChorus && this.activeVerseIndex % 2 === 0) {
              if (this.activeVerseIndex < (this.verseList.length - 1)) {
                this.activeVerseIndex++;
              } else {
                this.activeVerseIndex--;
              }
              this.visibleSectionIndex = 0;
              this.setActiveVerse();
              setTimeout(() => { this.ref.detectChanges(); }, 1);
              this.resetAndCheckFontSize();
            }
            break;
          case 'd':
          case 'D':
            this.hymnSelectionService.dialpad();
            break;
          case 'l':
          case 'L':
            this.sidenavService.toggleList();
            break;
          case 'w':
          case 'W':
            this.hymnSelectionService.goToRandomHymn(false);
            break;
          case 'f':
          case 'F':
            this.layoutService.fullscreen ?
              this.layoutService.exitFullScreen() : this.layoutService.enterFullScreen();
            break;
          case 'g':
          case 'G':
            this.hymnSelectionService.goToRandomHymn(true);
            break;
          case 's':
          case 'S':
            this.midiService.playing ?
              this.midiService.stop() : this.midiService.playMidi(this.hymn, this.settingService.getInstruments(), this.hymn.verses.length);
            break;
          case '+':
            // FIX: Now increases lines per verse instead of font size
            if (this.linesPerVerse < 7) {
              this.applyLinesPerVerseChange(this.linesPerVerse + 1);
            }
            break;
          case '-':
            // FIX: Now decreases lines per verse instead of font size
            if (this.linesPerVerse > 1) {
              this.applyLinesPerVerseChange(this.linesPerVerse - 1);
            }
            break;
          case '<':
          case ',':
            this.midiService.decreasePlaybackSpeed();
            break;
          case '>':
          case '.':
            this.midiService.increasePlaybackSpeed();
            break;
          case 'm':
            this.midiService.setPlaybackSpeed(1.0);
            break;
          case 'v':
          case 'V':
            this.router.navigate([`/${translation.path_hymn}/`+this.hymn.hymnNumber]);
            break;
          case 'i':
            if(!this.modalService.active) {
              this.modalService.openDialog('shortcuts')
            } else {
              this.modalService.closeDialog();
            }
            break;
          case 'Escape':
            if(this.modalService.active) {
              this.modalService.closeDialog();
            }
            break;
          default:
            if (!isNaN(+event.key)) {
              const verseNumber = Number(event.key);
              if (verseNumber > 0 && verseNumber <= this.hymn.numberOfVerses) {
                if (this.shouldBlockNavigation()) {
                  this.showNavigationBlockedMessage();
                } else {
                  if (this.hymn.hasChorus) {
                    this.activeVerseIndex = (verseNumber - 1) * 2;
                  } else {
                    this.activeVerseIndex = verseNumber - 1;
                  }
                  this.visibleSectionIndex = 0;
                  this.setActiveVerse();
                  setTimeout(() => { this.ref.detectChanges(); }, 1);
                  this.resetAndCheckFontSize();
                }
              }
            }
        }
      } else {
        switch (event.key) {
          case 'd':
          case 'D':
            this.hymnSelectionService.dialpadCancel();
            break;
          case 'Escape':
            this.hymnSelectionService.dialpadCancel();
            this.hymnSelectionService.searchFocus = false;
            break;
        }
      }
    });
  }

  checkFontSize() {
    if (this.maxFontSize == null) {
      this.contentHeight = this.content.nativeElement.scrollHeight;
      this.containerHeight = this.content.nativeElement.clientHeight;

      if (this.contentHeight <= this.containerHeight && this.fontsize <= 1000) {
        while (this.contentHeight <= this.containerHeight && this.fontsize <= 200 && (this.crossVerseConsistentFontSize == null || this.fontsize < this.crossVerseConsistentFontSize)) {
          this.fontsize++;
          this.setHeights();
          this.contentHeight = this.content.nativeElement.scrollHeight;
          this.containerHeight = this.content.nativeElement.clientHeight;
          this.ref.detectChanges();
          if (this.contentHeight === 0) break;
        }

        while (this.contentHeight > this.containerHeight) {
          this.fontsize--;
          this.setHeights();
          this.contentHeight = this.content.nativeElement.scrollHeight;
          this.containerHeight = this.content.nativeElement.clientHeight;
          this.ref.detectChanges();
          if (this.contentHeight === 0) break;
        }

        this.maxFontSize = this.fontsize;
        if(this.crossVerseConsistentFontSize == null || this.maxFontSize < this.crossVerseConsistentFontSize) {
          this.crossVerseConsistentFontSize = this.fontsize;
        }
      }
      else {
        while (this.fontsize > 7 && this.contentHeight > this.containerHeight && this.fontsize) {
          this.fontsize--;
          this.setHeights();
          this.contentHeight = this.content.nativeElement.scrollHeight;
          this.containerHeight = this.content.nativeElement.clientHeight;
          this.ref.detectChanges();
          if (this.contentHeight === 0) break;
        }

        this.maxFontSize = this.fontsize;
        if(this.crossVerseConsistentFontSize == null || this.maxFontSize < this.crossVerseConsistentFontSize) {
          this.crossVerseConsistentFontSize = this.fontsize;
        }
      }
    }
  }

  loadActiveVerseLines() {
    this.visibleVerseSections.clear();
    this.currentVerseSectionMap.clear();

    const verseContents = this.getLinesForVerse(this.activeVerse);

    if(verseContents) {
      this.activeVerseLines = verseContents;

      for (let i = 0; i < this.activeVerseLines.length; i++) {
        const sectionIndex = Math.floor(i / this.linesPerVerse);
        let section: HymnSection | undefined;

        if (this.visibleVerseSections.has(sectionIndex)) {
          section = this.visibleVerseSections.get(sectionIndex);
        } else {
          section = {lines: []};
          this.visibleVerseSections.set(sectionIndex, section);
        }

        if(section) {
          section.lines[i % this.linesPerVerse] = this.activeVerseLines[i];
        }

        const line = this.activeVerseLines[i];
        const regex = /<(\d+):([0-9,]+)>/g;
        let match;
        while ((match = regex.exec(line)) !== null) {
          const track = match[1];
          const indices = match[2].split(',');
          for (const idx of indices) {
            this.currentVerseSectionMap.set(`${track}:${idx}`, sectionIndex);
          }
        }
      }

      const size = this.visibleVerseSections.size;
      if (size > 1) {
        for (let i = 0; i < size; i++) {
          let section = this.visibleVerseSections.get(i);
          if (section && i < size - 1) {
            section.lines[section.lines.length - 1] += ' ...';
          }
          if (section && i > 0) {
            section.lines[0] = '... ' + section.lines[0];
          }
        }
      }
    }

    setTimeout(() => this.rebuildKaraokeIndex(), 50);
  }

  changeFontSize() {
    this.layoutService.fontSizeSheetActive = true;

    const bottomSheetRef = this.bottomSheet.open(FontSizeComponent, {
      data: {
        title: translation.lines_per_verse,
        fontSizeEnabled: false,
        linesPerVerseEnabled: true,
        linesPerVerseMin: 1,
        linesPerVerseMax: 7,
        currentLinesPerVerse: this.settingService.getPresentationModeVisibleLines(),
        linesPerVerseChange: new EventEmitter<number>(),
        fonts: this.settingService.getAvailableFonts(),
        currentFontFamily: this.settingService.getFontFamily(),
        fontFamilyChange: new EventEmitter<string | undefined>(),
        fontWeightEnabled: true,
        currentFontWeight: this.settingService.getFontWeight(),
        fontWeightChange: new EventEmitter<number | undefined>(),
        textOutlineToggleEnabled: true,
        textOutlineEnabled: this.settingService.getTextOutlineEnabled(),
        textOutlineChange: new EventEmitter<boolean>(),
        textAlignmentEnabled: true,
        currentTextAlignment: this.settingService.getTextAlignmentPresentationMode(),
        textAlignmentChange: new EventEmitter<string>()
      },
    });

    bottomSheetRef.instance.data.linesPerVerseChange?.subscribe((linesPerVerse: number) => {
      this.applyLinesPerVerseChange(linesPerVerse);
    });

    bottomSheetRef.instance.data.fontFamilyChange?.subscribe((fam: string | undefined) => {
      this.settingService.setPresentationModeFontFamily(fam);
    });

    bottomSheetRef.instance.data.fontWeightChange?.subscribe((w: number | undefined) => {
      this.settingService.setFontWeight(w);
    });

    bottomSheetRef.instance.data.textOutlineChange?.subscribe((enabled: boolean) => {
      this.settingService.setTextOutlineEnabled(enabled);
    });

    bottomSheetRef.instance.data.textAlignmentChange?.subscribe((al: string) => {
      this.settingService.setTextAlignmentPresentationMode(al);
    });

    bottomSheetRef.afterDismissed().subscribe(() => {
      this.layoutService.fontSizeSheetActive = false;
      this.settingService.setPresentationModeVisibleLines(this.linesPerVerse);
    });
  }

  applyLinesPerVerseChange(linesPerVerse: number) {
    // Round to ensure we have an integer
    const targetLines = Math.round(linesPerVerse);

    // Safeguard the bounds
    if (targetLines < 1 || targetLines > 7) return;

    if (targetLines !== this.linesPerVerse) {
      this.linesPerVerse = targetLines;
      this.settingService.setPresentationModeVisibleLines(this.linesPerVerse);
      this.visibleSectionIndex = 0;
      this.loadActiveVerseLines();
      this.crossVerseConsistentFontSize = null;
      this.resetAndCheckFontSize();
      const msg = `${translation.lines_per_verse}: ${this.linesPerVerse}`;
      this.snackBar.open(msg, undefined, { duration: 1500 });
    }
  }

  // FIX: These methods are now mapped to lines per verse logic via the template
  previewLinesPerVerse(fontSize: number) {
    // Handle pinch-to-zoom mapping to lines per verse
    // Larger pinch "font size" values correspond to fewer lines per verse (more zoomed in)
    // Map font range [8, 100] to lines range [7, 1]
    const minLines = 1;
    const maxLines = 7;
    const minF = this.fontSizeMin;
    const maxF = this.fontSizeMax;

    // Linear interpolation: as fontSize increases, lines decrease
    const calculatedLines = maxLines - ((fontSize - minF) / (maxF - minF)) * (maxLines - minLines);

    this.applyLinesPerVerseChange(calculatedLines);
  }

  updateLinesPerVerse(fontSize: number) {
    this.previewLinesPerVerse(fontSize);
  }

  resetAndCheckFontSize() {
    setTimeout(() => {
      this.maxFontSize = null;
      this.checkFontSize();
    }, 10);
  }

  onResize() {
    this.crossVerseConsistentFontSize = null;
    this.resetAndCheckFontSize();
  }

  protected readonly translation = translation;

  async onDoubleTap() {
    if(this.layoutService.fullscreenSupported()) {
      await this.layoutService.toggleFullScreen();
    }
  }
}
